import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, MapPinned, ShieldCheck, ChevronDown, ArrowRight, Target, Megaphone, Handshake } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AddressSearch from "../components/AddressSearch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { fmtUSD } from "../lib/format";
import { usePageSeo } from "../lib/seo";
import { ENTITY_SITE, brokerageNode, agentNode, breadcrumbNode } from "../lib/entityGraph";
import MultiAiAvmSection from "../components/valuation/MultiAiAvmSection";
import HomePortalDemo from "../components/valuation/HomePortalDemo";
import DirectSaleHomeFeature from "../components/DirectSaleHomeFeature";
import { buildAvmFaq } from "../data/avmContent";
import api from "../lib/api";

const AVM_TAX = "the Santa Clara County documentary transfer tax of $1.10 per $1,000 (plus any city measure such as San Jose's Measure E or Mountain View's Measure G)";

const HERO_IMG = "/hero-agent.jpg";

const CITIES = ["San Jose", "Cupertino", "Santa Clara", "Palo Alto", "Los Gatos", "Saratoga", "Mountain View", "Campbell"];

const FAQS = [
  ["What's my home worth in Santa Clara County?", "Enter your address above to get a free, instant home value estimate for your Santa Clara County property, including a low–high range and recent comparable sales. It's an automated estimate — for a precise figure, request a broker-reviewed valuation from Equitas Homes."],
  ["How much does it cost to sell a home in Santa Clara County?", "Typical seller costs include the listing commission, optional buyer-side compensation, county and city transfer taxes (like San Jose Measure E or Mountain View Measure G), escrow and title fees, and any repairs or credits. With Equitas Homes' 1% listing fee instead of a traditional 3%, most sellers keep tens of thousands more. Use the calculator above to estimate your net proceeds."],
  ["Is this an official appraisal?", "No. Equitas Home Value provides an automated estimate for informational purposes only. It is not an appraisal or a guaranteed sale price. For a precise figure, request a broker-reviewed valuation."],
  ["Do I need to give my contact information to see my estimate?", "No. You can view your estimate, comparable sales, and full seller net sheet without registering. We only ask for your name and email when you request the downloadable PDF report."],
  ["What is the 1% listing fee?", "Equitas Homes provides full-service listing representation for a 1% listing fee — pricing strategy, MLS exposure, professional marketing, negotiation, and closing coordination. A comparison figure of 3% is an illustrative assumption, not a fixed or legally required rate."],
  ["How are the transfer taxes calculated?", "Our engine applies the Santa Clara County documentary transfer tax plus any applicable city conveyance tax (e.g., San Jose Measure E, Mountain View Measure G) based on the property's jurisdiction. All allocations are negotiable and editable."],
  ["Where do the property values come from?", "This demonstration uses clearly-labeled sample data. In production, Equitas connects to a licensed property-data provider. We never present sample values as live data."],
];

export default function Home() {
  const [savings, setSavings] = useState(1800000);
  const [latestPosts, setLatestPosts] = useState([]);
  useEffect(() => {
    api.get("/blog/latest", { params: { limit: 3 } })
      .then(({ data }) => setLatestPosts(data.posts || []))
      .catch(() => setLatestPosts([]));
  }, []);
  const allFaqs = [...FAQS, ...buildAvmFaq("Santa Clara County", AVM_TAX)];
  usePageSeo({
    title: "Santa Clara County Multi-AI & 3-Portal AVM Home Value Tool | Equitas Homes",
    description: "Calculate your Santa Clara County home value with our Multi-AI engine and 3-portal AVM comparison tool. Review instant valuations, local transfer taxes, and 1% listing savings.",
    canonical: `${ENTITY_SITE}/`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "WebApplication", "@id": `${ENTITY_SITE}/#app`, name: "Equitas Santa Clara County Multi-AI & 3-Portal AVM Tool", url: `${ENTITY_SITE}/`,
          applicationCategory: "RealEstateApplication", operatingSystem: "Web",
          description: "A free Santa Clara County home-valuation tool that benchmarks a live Zillow Zestimate®, Redfin, and Homes.com against a three-model AI appraisal panel (Gemini, OpenAI, Claude), applies local transfer taxes, and shows net equity at a 1% listing fee.",
          featureList: ["3-portal AVM benchmarking (Zillow, Redfin, Homes.com)", "Multi-model AI comparative market analysis", "Local transfer-tax calculation", "1% vs 3% net-equity comparison"],
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          creator: { "@id": `${ENTITY_SITE}/#brokerage` },
          areaServed: { "@type": "AdministrativeArea", name: "Santa Clara County, CA" } },
        brokerageNode(),
        agentNode(),
        { "@type": "FAQPage", "@id": `${ENTITY_SITE}/#faq`,
          mainEntity: allFaqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
        breadcrumbNode(`${ENTITY_SITE}/#breadcrumb`, [{ name: "Home", item: `${ENTITY_SITE}/` }]),
      ],
    },
  });
  const equitas = savings * 0.01;
  const trad = savings * 0.03;

  return (
    <div className="min-h-screen bg-brand-cream paper-grain">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Equitas real estate agent reviewing a home valuation with clients in front of a Silicon Valley home"
            className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 via-brand-dark/60 to-brand-dark/15" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="eyebrow text-brand-gold">Santa Clara &amp; San Mateo Counties · Multi-AI · 3-Portal AVM · 1% Listing Fee</span>
            <h1 className="mt-4 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.45)]">
              Discover Your Home's Value.<br />
              <span className="text-brand-gold">Keep More of Your Equity.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed drop-shadow-[0_1px_10px_rgba(0,0,0,0.4)]">
              Get a personalized home value estimate, explore comparable sales, and see what you could keep when you sell with Equitas Homes.
            </p>
            <p className="mt-3 max-w-xl text-sm text-white/70 leading-relaxed drop-shadow-[0_1px_10px_rgba(0,0,0,0.4)]">
              Our tool benchmarks 3 real-estate portals and a multi-model AI panel against Santa Clara &amp; San Mateo County transfer taxes — so you see a defensible value and the equity you keep.
            </p>
            <div className="mt-8 max-w-3xl">
              <AddressSearch large />
            </div>
            <Link to="/services" data-testid="hero-what-we-do-link"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand-gold hover:text-white transition-colors drop-shadow-[0_1px_10px_rgba(0,0,0,0.4)]">
              See what we do — full service at a 1% listing fee <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              {CITIES.map((c) => (
                <span key={c} className="text-xs text-white/70 uppercase tracking-wider">{c}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust strip — as seen across the major portals */}
      <div className="border-y border-brand-hairline bg-white/70" data-testid="trust-strip">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex flex-wrap items-center gap-x-6 gap-y-2 justify-center sm:justify-start">
          <span className="text-xs uppercase tracking-wider text-brand-stone/70">Benchmarked across</span>
          <span className="font-serif text-lg text-brand-ink">Zillow</span>
          <span className="text-brand-hairline">·</span>
          <span className="font-serif text-lg text-brand-ink">Redfin</span>
          <span className="text-brand-hairline">·</span>
          <span className="font-serif text-lg text-brand-ink">Homes.com</span>
          <span className="text-xs text-brand-stone/60">+ a 3-model AI appraisal panel</span>
        </div>
      </div>

      {/* The Equitas Difference — seller-equity brand positioning */}
      <section className="bg-brand-cream" data-testid="equitas-difference">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="eyebrow text-brand-gold">The Equitas Difference</span>
            <h2 className="mt-4 font-serif font-light leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-brand-ink">
              The Brokerage Designed Around<br className="hidden sm:block" /> Protecting Seller Equity.
            </h2>
            <div className="mt-7 space-y-5 text-brand-stone leading-relaxed text-base sm:text-lg">
              <p>Selling your home isn't just about the final sale price. From pricing and preparation to marketing, negotiation, commissions and closing costs, every decision can affect how much equity you ultimately keep.</p>
              <p className="text-brand-ink">Equitas Homes is built around protecting it.</p>
            </div>
            <p className="mt-7 font-serif text-xl sm:text-2xl text-brand-emerald leading-snug">
              It's not just what you sell for—<span className="text-brand-gold">it's what you keep.</span>
            </p>
          </motion.div>

          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-brand-hairline bg-brand-hairline sm:grid-cols-2 lg:grid-cols-4">
            {[
              [Target, "Protect Your Price", "Local pricing, preparation and market strategy designed to position your home effectively."],
              [Megaphone, "Protect Your Exposure", "Professional marketing and broad MLS distribution designed to reach the market."],
              [Handshake, "Protect Your Negotiation", "Experienced representation through offers, inspections, concessions and escrow."],
              [ShieldCheck, "Protect Your Equity", "Full-service representation for a 1% listing fee, helping reduce one of the largest costs of selling."],
            ].map(([Icon, title, body], i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-brand-cream p-8 lg:p-9" data-testid="equity-pillar">
                <Icon className="h-6 w-6 text-brand-gold" strokeWidth={1.75} />
                <h3 className="mt-5 font-serif text-xl text-brand-ink">{title}</h3>
                <p className="mt-3 text-sm text-brand-stone leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial transition bridge → valuation technology */}
      <section className="bg-brand-cream" data-testid="equity-transition">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 pb-16 lg:pb-20 text-center">
          <p className="font-serif font-light text-2xl sm:text-3xl text-brand-ink leading-snug">
            Better information helps <span className="text-brand-gold">protect your equity.</span>
          </p>
          <p className="mt-4 text-base sm:text-lg text-brand-stone leading-relaxed">
            Our valuation technology compares multiple data sources to help sellers understand not only what their home may be worth, but what they may actually keep.
          </p>
        </div>
      </section>

      {/* Multi-AI & 3-Portal AVM: AEO answer + methodology (blocks 2 & 3) */}
      <MultiAiAvmSection locationName="Santa Clara &amp; San Mateo County" taxMeasure={AVM_TAX} />

      {/* Feature bento */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            [TrendingUp, "Automated Valuation", "An instant estimate with a low–high range, confidence, and a clear source and date — no guesswork, no jargon."],
            [MapPinned, "Local Tax Accuracy", "Jurisdiction-aware transfer taxes for Santa Clara County, including San Jose Measure E and Mountain View Measure G."],
            [ShieldCheck, "1% Equity Retention", "See exactly what you keep with a 1% listing fee compared to a traditional 3% assumption."],
          ].map(([Icon, title, body], i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-2xl bg-white border border-brand-hairline p-8 shadow-[0_4px_20px_rgba(27,59,43,0.04)]">
              <Icon className="h-7 w-7 text-brand-gold" />
              <h3 className="mt-5 font-serif text-2xl text-brand-ink">{title}</h3>
              <p className="mt-3 text-sm text-brand-stone leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3-Portal comparison demo */}
      <HomePortalDemo />

      {/* Savings teaser */}
      <section className="bg-brand-emerald text-brand-cream">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Commission Comparison</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-light">See what a 1% listing fee keeps in your pocket.</h2>
            <p className="mt-5 text-brand-cream/70 max-w-lg leading-relaxed">
              Drag to your estimated sale price. Comparison figures are illustrative assumptions — all commissions are negotiable and not set by law.
            </p>
          </div>
          <div className="rounded-2xl bg-brand-dark/60 border border-brand-cream/10 p-8">
            <label className="text-xs uppercase tracking-[0.2em] text-brand-cream/60" htmlFor="savings-slider">Estimated sale price</label>
            <p className="mt-2 font-mono text-3xl sm:text-4xl font-bold">{fmtUSD(savings)}</p>
            <input id="savings-slider" data-testid="home-savings-slider" type="range" min={500000} max={5000000} step={50000}
              value={savings} onChange={(e) => setSavings(Number(e.target.value))}
              className="mt-6 w-full accent-brand-gold" />
            <div className="mt-8 grid grid-cols-3 gap-4 text-center">
              <div><p className="text-xs text-brand-cream/60">Illustrative 3% Fee</p><p className="mt-1 font-mono text-lg">{fmtUSD(trad)}</p></div>
              <div><p className="text-xs text-brand-cream/60">Equitas 1%</p><p className="mt-1 font-mono text-lg text-brand-gold">{fmtUSD(equitas)}</p></div>
              <div><p className="text-xs text-brand-cream/60">You Keep</p><p className="mt-1 font-mono text-lg font-bold" data-testid="home-savings-amount">{fmtUSD(trad - equitas)}</p></div>
            </div>
            <p className="mt-6 text-[0.7rem] leading-relaxed text-brand-cream/50" data-testid="home-slider-disclaimer">Comparison figures are illustrative assumptions — all commissions and fees are negotiable and not set by law.</p>
          </div>
        </div>
      </section>

      {/* EEAT trust band */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16">
        <div className="rounded-2xl border border-brand-hairline bg-white p-6 sm:p-10 flex flex-col sm:flex-row items-start gap-6 shadow-[0_4px_20px_rgba(11,30,140,0.04)]">
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_home-value-estimate/artifacts/tkfajn1r_EQ1%24FAVICON.png" alt="Equitas 1%" className="h-16 w-16 rounded-full shrink-0" />
          <div>
            <span className="eyebrow">Local Expertise You Can Trust</span>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-brand-ink">Prepared by a licensed Santa Clara County broker</h2>
            <p className="mt-3 text-sm sm:text-base text-brand-stone leading-relaxed max-w-3xl">
              Estimates on this site are provided by Equitas Homes and can be reviewed by <strong>Louis Fong, Broker</strong> (California <strong>DRE 01151598</strong>), who helps sellers across San Jose, Cupertino, Palo Alto, Saratoga, Los Gatos and all of Santa Clara County. We're transparent: automated estimates are clearly labeled and never presented as a guaranteed sale price, and your report request never enrolls you in marketing.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <a href="https://equitashomes.com" className="text-brand-emerald font-medium hover:underline" data-testid="eeat-main-site">About Equitas Homes →</a>
              <a href="tel:+14085611321" className="text-brand-emerald font-medium hover:underline">(408) 561-1321</a>
              <a href="mailto:louis@equitashomes.com" className="text-brand-emerald font-medium hover:underline">louis@equitashomes.com</a>
            </div>
          </div>
        </div>
      </section>

      {/* 2% Direct Sale feature */}
      <DirectSaleHomeFeature />

      {/* What We Do banner */}
      <section className="bg-brand-dark text-brand-cream" data-testid="home-what-we-do-banner">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 lg:py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <span className="eyebrow text-brand-gold">What We Do</span>
            <h2 className="mt-3 font-serif font-light text-3xl sm:text-4xl leading-tight">
              Full-service representation — from pre-market strategy to escrow closing.
            </h2>
            <p className="mt-4 text-brand-cream/75 leading-relaxed">
              Every listing is handled directly by Broker Louis Fong for a 1% listing fee, with a 2% Direct Sale program for off-market deals. See exactly how we protect your equity.
            </p>
          </div>
          <Link to="/services" data-testid="home-what-we-do-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold px-6 py-3.5 text-sm font-semibold text-brand-dark transition-colors hover:bg-brand-gold/90 shrink-0">
            Explore what we do <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-16 lg:py-24">
        <span className="eyebrow">Frequently Asked</span>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-brand-ink">Seller questions, answered.</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
          {allFaqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-brand-hairline">
              <AccordionTrigger className="text-left font-serif text-xl text-brand-ink hover:text-brand-emerald" data-testid={`faq-trigger-${i}`}>
                {q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-stone text-sm leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Latest from the blog */}
      {latestPosts.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16 lg:pb-24" data-testid="home-blog-teaser">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="eyebrow">From the Blog</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-brand-ink">Silicon Valley seller insights</h2>
            </div>
            <Link to="/blog" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-brand-emerald hover:underline shrink-0" data-testid="home-blog-all-link">
              All articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {latestPosts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid="home-blog-card"
                className="group flex flex-col rounded-2xl bg-white border border-brand-hairline p-6 shadow-[0_4px_20px_rgba(27,59,43,0.04)] hover:border-brand-emerald transition-colors">
                {p.category && <span className="text-[0.65rem] uppercase tracking-wider text-brand-gold font-semibold">{p.category}</span>}
                <h3 className="mt-2 font-serif text-xl text-brand-ink leading-snug group-hover:text-brand-emerald transition-colors line-clamp-3">{p.title}</h3>
                <p className="mt-3 text-sm text-brand-stone leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-brand-emerald">Read article <ArrowRight className="h-3.5 w-3.5" /></span>
              </Link>
            ))}
          </div>
          <Link to="/blog" className="mt-6 sm:hidden inline-flex items-center gap-1.5 text-sm font-medium text-brand-emerald hover:underline">
            All articles <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      )}

      <Footer />
    </div>
  );
}
